import { FC, useState, useEffect, useRef } from 'react';
import { Loading } from '@maverick/ui';
import { MainForm, GuestInfoForm, SeatingSuccess } from './steps';
import { SeatingReduxProps } from '.';
import { SeatingData } from '@maverick/entity';
import { GetQueryStringParam } from '@maverick/utils';
import { LocationManager } from '../../../manager/Location.manager';
import { useRouter } from 'next/router';

import * as Styled from './Seating.styled';

export enum SeatingSteps {
	Main = 'main',
	GuestInfo = 'guest-info',
	Success = 'success',
}

interface SeatingProps extends SeatingReduxProps {}
export const Seating: FC<SeatingProps> = ({
	locationStatus,
	restaurant,
	seatingData,
	yextId,
	setSeatingData,
	setSeatingDataModify,
}) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [step, setStep] = useState<SeatingSteps>(SeatingSteps.Main);
	const customLocation = useRef<boolean>(false);
	const router = useRouter();
	const goToYext = useRef<boolean>(false);

	useEffect(() => {
		initFromQueryParams();

		return () => {
			setSeatingDataModify(null);
		};
	}, []);

	useEffect(() => {
		if (customLocation.current) {
			return;
		}

		if (['state', 'loading'].includes(locationStatus)) {
			setLoading(true);
		} else if (locationStatus === 'done') {
			initLocation();
		}
	}, [locationStatus]);

	useEffect(() => {
		if (router.isReady) {
			if (goToYext.current) {
				window.location.replace('https://locations.outback.com/search');
				return;
			}
		}
	}, [router]);

	const initLocation = async () => {
		if (!yextId || !restaurant) {
			goToYext.current = true;
			return;
		}

		if (!seatingData || seatingData.LocationId !== restaurant.extref?.split?.('_')?.[1]) {
			setSeatingData(
				new SeatingData({
					LocationId: yextId ?? undefined,
					LocationName: restaurant.name,
					LocationPhoneNumber: restaurant.telephone,
				})
			);
		}
		setLoading(false);
	};

	const initFromQueryParams = async () => {
		const unitId = GetQueryStringParam('unitid') || GetQueryStringParam('unitId');
		if (!unitId) {
			return;
		}
		customLocation.current = true;

		setLoading(true);
		const restaurant = await LocationManager.GetLocationByRef(unitId);
		setLoading(false);

		if (!restaurant) {
			window.location.replace('https://locations.outback.com/search');
			return;
		}

		setSeatingData(
			new SeatingData({
				LocationId: unitId,
				LocationName: restaurant.name,
				LocationPhoneNumber: restaurant.telephone,
			})
		);
	};

	return (
		<Styled.Container>
			{loading && (
				<Styled.Loading>
					<Loading />
				</Styled.Loading>
			)}

			{!!seatingData && (
				<>
					{step === SeatingSteps.Main && <MainForm onNextClick={() => setStep(SeatingSteps.GuestInfo)} />}
					{step === SeatingSteps.GuestInfo && (
						<GuestInfoForm
							onBackClick={() => setStep(SeatingSteps.Main)}
							setLoading={setLoading}
							onSuccess={() => setStep(SeatingSteps.Success)}
						/>
					)}
					{step === SeatingSteps.Success && <SeatingSuccess />}
				</>
			)}
		</Styled.Container>
	);
};
